import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="timezone" on the "Change how it works" form. Never silently
// overwrites the user's saved time zone: on load it only offers a one-click hint if the browser's
// detected IANA zone (via Intl) differs from what's stored, and only recomputes the displayed
// weekday/time when the zone select actually changes -- whether from that hint or the user picking
// a country/city themselves -- so what's shown always matches what's about to be submitted.
// Onboarding sets autoDetect: a new user only has the UTC default, so the detected zone is applied
// right away instead of offered.
export default class extends Controller {
    static targets = ["weekday", "time", "zoneSelect", "hint", "hintZone"]
    static values = { utcWeekday: String, utcTime: String, currentZone: String, autoDetect: Boolean }

    connect() {
        this.detected = Intl.DateTimeFormat().resolvedOptions().timeZone
        if (!this.detected || this.detected === this.currentZoneValue) return
        if (!this.hasZoneOption(this.detected)) return

        if (this.autoDetectValue) {
            this.useDetected()
            return
        }

        this.hintZoneTarget.textContent = this.detected
        this.hintTarget.classList.remove("hidden")
    }

    useDetected() {
        this.zoneSelectTarget.value = this.detected
        this.zoneChanged()
        if (this.hasHintTarget) this.hintTarget.classList.add("hidden")
    }

    zoneChanged() {
        const [hour, minute] = this.utcTimeValue.split(":").map(Number)
        const weekdays = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"]
        const targetDow = weekdays.indexOf(this.utcWeekdayValue)

        const now = new Date()
        let daysAhead = (targetDow - now.getUTCDay() + 7) % 7
        if (daysAhead === 0) daysAhead = 7 // mirrors Rails' Date#next_occurring: never today
        const utcAt = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + daysAhead, hour, minute))

        const parts = new Intl.DateTimeFormat("en-US", {
            timeZone: this.zoneSelectTarget.value, weekday: "long", hour: "2-digit", minute: "2-digit", hourCycle: "h23"
        }).formatToParts(utcAt)
        const partValue = (type) => parts.find((p) => p.type === type).value

        this.weekdayTarget.value = partValue("weekday").toLowerCase()
        this.timeTarget.value = `${partValue("hour")}:${partValue("minute")}`
    }

    hasZoneOption(value) {
        return Array.from(this.zoneSelectTarget.options).some((option) => option.value === value)
    }
}
